import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook for managing editable title state and handlers
 * Uses caretRangeFromPoint for accurate cursor positioning on click.
 * 
 * Shared hook used by CampaignHeader for inline title editing.
 * 
 * @param {string} initialValue - The initial title value
 * @param {Function} onSave - Callback when title is saved (receives new title)
 * @returns {Object} Title editing state and handlers
 */
const useTitleEditor = (initialValue, onSave) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(initialValue);
  const inputRef = useRef(null);
  const textRef = useRef(null); // Ref for the text element (span/h2)
  const clickCursorPosRef = useRef(null); // Store cursor position from click

  // Sync value when external value changes (and not editing)
  useEffect(() => {
    if (!isEditing) {
      setEditValue(initialValue);
    }
  }, [initialValue, isEditing]);

  // Focus and position cursor when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      const pos = clickCursorPosRef.current ?? inputRef.current.value.length;
      inputRef.current.setSelectionRange(pos, pos);
      clickCursorPosRef.current = null;
    }
  }, [isEditing]);

  const handleClick = useCallback((e) => {
    if (!onSave) return;
    
    // Default to end of text
    let cursorPos = editValue.length;
    
    // Calculate cursor position from click location using caretRangeFromPoint
    // This API returns the character offset at the clicked position
    const textElement = textRef.current;
    
    if (textElement && document.caretRangeFromPoint) {
      try {
        const range = document.caretRangeFromPoint(e.clientX, e.clientY);
        if (range) {
          // Check if the click was within our text element
          // range.startContainer is usually the text node (child of the element)
          const container = range.startContainer;
          if (textElement === container || textElement.contains(container)) {
            cursorPos = range.startOffset;
          }
        }
      } catch (err) {
        // Fallback to end on any error
      }
    }
    
    clickCursorPosRef.current = cursorPos;
    setIsEditing(true);
  }, [onSave, editValue.length]);

  const handleSave = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== initialValue) {
      onSave?.(trimmed);
    } else {
      setEditValue(initialValue);
    }
    setIsEditing(false);
  }, [editValue, initialValue, onSave]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(initialValue);
      setIsEditing(false);
    }
  }, [handleSave, initialValue]);

  const handleChange = useCallback((e) => {
    setEditValue(e.target.value);
  }, []);

  return {
    isEditing,
    editValue,
    inputRef,
    textRef, // Expose ref for the text element
    handleClick,
    handleSave,
    handleKeyDown,
    handleChange,
    canEdit: !!onSave,
  };
};

export default useTitleEditor;
